import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import AdminTextInput from '../../../components/admin/admin-text-input';
import AdminSelect from '../../../components/admin/admin-default-select';
import AdminPhone from '../../../components/admin/admin-phone';
import * as distrctSiteUserApi from '../../../api/districtsiteuserapi';
import { getAllSchools } from '../../../api/schoolsApi';
import Loader from '../../../components/Loader.js';
import { getById } from '../../../api/districtapi';

export function UserRoleModal({ show, onClose, districtId, isEditMode = false, handleAddSiteUser, existingSiteUser = null, isDistrictGlobalAdmin }) {
    if (!show) return null;
    const [loadingData, setLoadingData] = useState(true);
    const [user, setUser] = useState({
        siteUserId: null,
        role: '',
        companyName: '',
        phone: '',
        email: '',
        fullName: '',
        notify: false,
        schoolUsersList: [],
        bAdminPermission: false,
        bApproverPermission: false,
        isReviewer : false,
    });

    const [districtName, setDistrictName] = useState('');
    const [schools, setSchools] = useState([]);
    const [districtUsers, setDistrictUsers] = useState([]);
    const [createDistrictAdmin, setCreateDistrictAdmin] = useState(false);
    const [validation, setValidation] = useState({
        role: true,
        siteUserId: true,
        fullName: true,
        email: true,
        phone: true,
        companyName: true,
        permissions: true
    });

    useEffect(() => {
        if (districtId) {
            getById(districtId)
                .then((response) => {
                    if (response.data.success) {                       
                        setDistrictName(response.data.data.name);
                    }
                })
                .catch(ex => {
                    console.log(ex);
                });
        }
    }, [districtId]);

    useEffect(() => {

        const loadInitialData = async () => {
            setLoadingData(true);
            try {
                // Fetch Schools
                const schoolsResponse = await getAllSchools(districtId);
                if (schoolsResponse.data.success) {
                    setSchools(schoolsResponse.data.data);
                    if (!existingSiteUser) {
                        setUser(prev => ({ ...prev, schoolUsersList: [] }));
                    }
                }

                // Fetch existing district users
                const usersResponse = await distrctSiteUserApi.getAllSchoolUsersByDistrictId(districtId);
                if (usersResponse.data.success) {
                    let users = usersResponse.data.data.map(a => ({
                        id: a.siteUsers.id,
                        email: a.siteUsers.email,
                        fullName: a.siteUsers.fullName,
                        siteUsers: a.siteUsers,
                        schoolUsersList: a.schoolUsersList,
                        isReviewer: a.isReviewer
                    }));
                    users = users.filter((a, i, s) => s.findIndex(b => b.id === a.id) === i);
                    setDistrictUsers(users);
                }
            } catch (err) {
                console.error('Error loading modal data:', err);
            } finally {
                setLoadingData(false);
            }
        };

        loadInitialData();
    }, [districtId, existingSiteUser]);



    useEffect(() => {
        if (existingSiteUser) { 
            setUser({
                siteUserId: existingSiteUser.siteUserId,
                role: existingSiteUser.role,
                fullName: existingSiteUser.siteUsers.fullName,
                email: existingSiteUser.siteUsers.email,
                phone: existingSiteUser.siteUsers.phone,
                companyName: existingSiteUser.siteUsers.organization,
                notify: existingSiteUser.notify,
                schoolUsersList: existingSiteUser.schoolUsersList || [],
                bAdminPermission: existingSiteUser.bAdminPermission,
                bApproverPermission: existingSiteUser.bApproverPermission,
                isReviewer: existingSiteUser.isReviewer,
            });
            setCreateDistrictAdmin(true);
        }
    }, [existingSiteUser]);

    const handleSelectingDistrictUser = (value) => {
        const selectedDistrictUser = districtUsers.find(u => u.id === value);
        if (value === "") {
            setUser(prev => ({ ...prev, siteUserId: null, bApproverPermission: false, schoolUsersList: [] }));
        } else if (value === "new") {
            setCreateDistrictAdmin(true);
            setUser(prev => ({ ...prev, siteUserId: null, bApproverPermission: false, schoolUsersList: [] }));
        } else {
            setCreateDistrictAdmin(false);
            setValidation({ ...validation, siteUserId: value != '' });
            setUser(prev => ({
                ...prev,
                siteUserId: value,
                bApproverPermission: selectedDistrictUser?.siteUsers?.isApprover,
                schoolUsersList: selectedDistrictUser?.schoolUsersList || []
            }));
        }
    };

    const handleNotifyChange = () => {
        setUser({ ...user, notify: !user.notify });
    };
     
    const handleApproverPermission = () => {
        setUser(prev => ({ ...prev, bApproverPermission: !prev.bApproverPermission }));
    };

    const handleSave = () => {
        let isValid = true;
        const validate = {
            role: true,
            siteUserId: true,
            fullName: true,
            email: true,
            phone: true,
            companyName: true,
            password: true,
            permissions: true
        };

        if (!user.role) {
            validate.role = false;
            isValid = false;
        }

        if (createDistrictAdmin) {
            if (!user.fullName) { validate.fullName = false; isValid = false; }
            if (!user.email) { validate.email = false; isValid = false; }
            if (!user.phone) { validate.phone = false; isValid = false; }
        } else {
            if (!user.siteUserId) {
                validate.siteUserId = false;
                isValid = false;
            }
        }
        if (!isDistrictGlobalAdmin) { 
            user.bAdminPermission = true;
            user.bApproverPermission = true;
        } else if (!user.bAdminPermission && !user.bApproverPermission) {
            validate.permissions = false;
            isValid = false;
        } 

        setValidation(validate);
        if (!isValid) return;

        if (!createDistrictAdmin) {
            const selectedUser = districtUsers.find(u => u.id === user.siteUserId);
            const updatedUser = {
                DistrictId: districtId,
                SiteUserId: user.siteUserId,
                Role: user.role,
                Email: selectedUser?.siteUsers?.email || '',
                FullName: selectedUser?.siteUsers?.fullName || '',
                Phone: selectedUser?.siteUsers?.phone || '',
                CompanyName: selectedUser?.siteUsers?.organization || districtName || '',
                Password: user.password || '',
                Notify: user.notify,
                SchoolUsersList: user.schoolUsersList || [],
                bAdminPermission: user.bAdminPermission,
                bApproverPermission: user.bApproverPermission,
                isDistrictGlobalAdmin: isDistrictGlobalAdmin,
                isReviewer: selectedUser.isReviewer
            };
            handleAddSiteUser(updatedUser);

        } else {
            handleAddSiteUser({
                DistrictId: districtId,
                SiteUserId: user.siteUserId,
                Role: user.role,
                Email: user.email,
                FullName: user.fullName,
                Phone: user.phone,
                CompanyName: districtName,
                Password: user.password,
                Notify: user.notify,
                SchoolUsersList: user.schoolUsersList,
                bAdminPermission: user.bAdminPermission,
                bApproverPermission: user.bApproverPermission,
                isDistrictGlobalAdmin: isDistrictGlobalAdmin,
                isReviewer: user.isReviewer
            });
        }
        onClose();
    };

    const handleSchoolNotifyChange = (schoolId) => {
        setUser(prev => {
            const existing = prev.schoolUsersList.find(s => s.districtSchoolId === schoolId);
            let updatedList;

            if (existing) {
                updatedList = prev.schoolUsersList.filter(s => s.districtSchoolId !== schoolId);
            } else {
                updatedList = [
                    ...prev.schoolUsersList,
                    { siteUserId: user.siteUserId, districtId: districtId, districtSchoolId: schoolId }
                ];
            }

            return {
                ...prev,
                schoolUsersList: updatedList
            };
        });
    };

 

    return (
        <div className="admin-modal-overlay">
            <div className="admin-modal admin-add-role">
                {loadingData ? <Loader /> : <>
                    <div className="admin-modal-scroll">
                        <button className="admin-modal-close" onClick={onClose}>
                            <span className="material-symbols-rounded">close</span>
                        </button>
                        <h2 className="admin-modal-heading">{isEditMode ? 'Edit District Role' : 'Add District Role'}</h2>
                        {isDistrictGlobalAdmin && (
                            <div className={`${validation.permissions ? '' : 'input-validation-error'}`}>
                                <label className="admin-default-label">District Permissions</label>
                                <div className="admin-modal-row"> 
                                    <label className="admin-inline-label" htmlFor="districtApprover">
                                        <input
                                            id="districtApprover"
                                            type="checkbox"
                                            checked={user.bApproverPermission}
                                            onChange={handleApproverPermission}
                                        />
                                        <span className="admin-inline-label-text">Approver</span>
                                    </label>
                                </div>
                                {!validation.permissions && (
                                    <div className="validation-error-message">At least one permission (Admin or Approver) is required.</div>
                                )}
                            </div>
                        )}

                        {!isEditMode && (
                            <div className="admin-modal-row">
                                <div className="admin-modal-cell">
                                    <AdminSelect
                                        id="district-user-select"
                                        label="Select Account"
                                        value={createDistrictAdmin ? 'new' : user.siteUserId || ''}
                                        onChange={handleSelectingDistrictUser}
                                        options={[
                                            { value: '', label: '[Select a District User]' },
                                            ...districtUsers.map(a => ({
                                                value: a.id,
                                                label: `${a.fullName} (${a.email})`
                                            })),
                                            { value: 'new', label: 'Create New District User' }
                                        ]}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="admin-modal-row">
                            <div className="admin-modal-cell">
                                <AdminSelect
                                    id="role"
                                    label="Job Title"
                                    isRequired
                                    value={user.role}
                                    validated={validation.role}
                                    onChange={value => {
                                        setUser(prev => ({ ...prev, role: value }));
                                        setValidation(prev => ({ ...prev, role: !!value }));
                                    }}
                                    requiredText="Role is required"
                                    options={[
                                        { value: '', label: '[Select A Role]' },
                                        { value: 'Administrator' },
                                        { value: 'Purchasing Principal' },
                                        { value: 'Educator' },
                                        { value: 'Facilities Team' }
                                    ]}
                                />
                            </div>
                        </div>

                        {(createDistrictAdmin || isEditMode) && (
                            <>
                                <div className="admin-modal-row">
                                    <div className="admin-modal-cell">
                                        <AdminTextInput
                                            id="full-name"
                                            label="First & Last Name"
                                            placeholder=""
                                            isRequired={true}
                                            value={user.fullName ?? ''}
                                            validated={validation.fullName}
                                            onChange={(value) => {
                                                setUser({ ...user, fullName: value });
                                                setValidation({ ...validation, fullName: value != '' });
                                            }}
                                            requiredText="First & Last Name is required"
                                        />
                                    </div>
                                </div>
                                <div className="admin-modal-row">
                                    <div className="admin-modal-cell">
                                        <AdminTextInput
                                            id="email-address"
                                            label="Email Address"
                                            placeholder=""
                                            isRequired={true}
                                            value={user.email ?? ''}
                                            validated={validation.email}
                                            onChange={(value) => {
                                                setUser({ ...user, email: value });
                                                setValidation({ ...validation, email: value != '' });
                                            }}
                                            requiredText="Email Address is required"
                                        />
                                    </div>
                                </div>
                                <div className="admin-modal-row">
                                    <div className="admin-modal-cell">
                                        <AdminPhone
                                            id="phone-number"
                                            label="Phone Number"
                                            type="phone"
                                            placeholder=""
                                            isRequired={true}
                                            value={user.phone ?? ''}
                                            loaded={(isEditMode == true && user.siteUserId != null) || isEditMode == false}
                                            validated={validation.phone}
                                            onChange={(value) => {
                                                setUser({ ...user, phone: value });
                                            }}
                                            onValidation={(valid) => { setValidation({ ...validation, phone: valid }); }}
                                            requiredText="A valid phone number is required"
                                        />
                                    </div>
                                </div>
                                <div className="admin-modal-row">
                                    <div className="admin-modal-cell">
                                        <AdminTextInput
                                            id="password"
                                            label="Password"
                                            inputType="password"
                                            placeholder="Leave blank to let user create their own password"
                                            isRequired={false}
                                            value={user.password ?? ''}
                                            onChange={(value) => {
                                                setUser({ ...user, password: value });
                                            }}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                        {isDistrictGlobalAdmin === false && schools.length > 0 && (
                            <label className="admin-default-label">Schools Name</label>
                        )}

                        {isDistrictGlobalAdmin === false && (
                            <div className="admin-modal-row">
                                {schools.length > 0 ? (
                                    schools.map((school) => (
                                        <label className="admin-inline-label" htmlFor={`school-notify-${school.baseId}`} key={school.baseId}>
                                            <input
                                                id={`school-notify-${school.baseId}`}
                                                type="checkbox"
                                                checked={user.schoolUsersList.some(s => s.districtSchoolId === school.baseId)}
                                                onChange={() => handleSchoolNotifyChange(school.baseId)}
                                            />
                                            <span className="admin-inline-label-text">{school.name}</span>
                                        </label>
                                    ))
                                ) : (
                                    <div>No active schools</div>
                                )}
                            </div>
                        )}
                        


                        <div className="admin-modal-row">
                            <div className="admin-modal-cell full">
                                <label className="admin-inline-label" htmlFor="notify">
                                    <input
                                        id="notify"
                                        type="checkbox"
                                        checked={user.notify}
                                        onChange={handleNotifyChange}
                                    />
                                    <span className="admin-inline-label-text">Do not receive RFQ requests</span>
                                </label>
                            </div>
                        </div>

                        <div className="admin-modal-button-wrap">
                            <button className="admin-modal-button" onClick={onClose}>Cancel</button>

                            <button className="admin-modal-button power" onClick={handleSave}>Save Changes</button>
                        </div>
                    </div>
                </>
                }
            </div>
        </div>
    );
}

UserRoleModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    districtId: PropTypes.number.isRequired,
    isEditMode: PropTypes.bool,
    handleAddSiteUser: PropTypes.func.isRequired,
    existingSiteUser: PropTypes.object,
    isDistrictGlobalAdmin: PropTypes.bool
};
